import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { appointmentAPI } from '../../lib/api';
import {
  VideoCamera,
  Clock,
  User,
  Stethoscope,
  CheckCircle,
  HourglassHigh,
  Phone,
  ChatCircle,
  ArrowLeft,
  FirstAidKit
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function WaitingRoomPage() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waitingStatus, setWaitingStatus] = useState('waiting'); // waiting, provider_ready, in_call
  const [waitTime, setWaitTime] = useState(0);

  const loadAppointment = useCallback(async () => {
    try {
      const res = await appointmentAPI.getById(appointmentId);
      setAppointment(res.data);
      
      // Check if provider has started the call
      if (res.data.call_status === 'provider_ready' || res.data.call_status === 'in_progress') {
        setWaitingStatus('provider_ready');
      }
    } catch (error) {
      console.error('Error loading appointment:', error);
      toast.error('Failed to load appointment');
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => {
    loadAppointment();
    
    // Poll for status updates every 10 seconds
    const pollInterval = setInterval(() => {
      loadAppointment();
    }, 10000);
    
    return () => clearInterval(pollInterval);
  }, [loadAppointment]);

  // Track wait time
  useEffect(() => {
    const timer = setInterval(() => {
      setWaitTime(prev => prev + 1);
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  const formatWaitTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const joinCall = () => {
    navigate(`/patient/video-call/${appointmentId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-slate-600">Loading your appointment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FirstAidKit size={32} weight="duotone" className="text-teal-600" />
            <div>
              <h1 className="font-bold text-slate-900">T-MEDCMA</h1>
              <p className="text-xs text-slate-500">Virtual Waiting Room</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/patient/appointments')}
            className="text-slate-600 hover:text-slate-900 flex items-center gap-2"
          >
            <ArrowLeft size={18} />
            Back to Appointments
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Waiting Room Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Status Banner */}
          <div className={`p-6 text-center ${
            waitingStatus === 'provider_ready' 
              ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
              : 'bg-gradient-to-r from-teal-500 to-cyan-500'
          } text-white`}>
            {waitingStatus === 'provider_ready' ? (
              <>
                <CheckCircle size={64} weight="duotone" className="mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-2">Your Provider is Ready!</h2>
                <p className="text-teal-100">Click the button below to join your video visit</p>
              </>
            ) : (
              <>
                <HourglassHigh size={64} weight="duotone" className="mx-auto mb-4 animate-pulse" />
                <h2 className="text-2xl font-bold mb-2">Please Wait</h2>
                <p className="text-teal-100">Your provider will be with you shortly</p>
              </>
            )}
          </div>

          {/* Appointment Details */}
          <div className="p-8">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Left - Appointment Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 text-lg flex items-center gap-2">
                  <Stethoscope size={20} className="text-teal-600" />
                  Appointment Details
                </h3>
                
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Clock size={18} className="text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Scheduled Time</p>
                      <p className="font-medium text-slate-900">
                        {appointment?.scheduled_time 
                          ? new Date(appointment.scheduled_time).toLocaleString('en-US', {
                              weekday: 'long',
                              month: 'long',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            })
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Stethoscope size={18} className="text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Visit Type</p>
                      <p className="font-medium text-slate-900 capitalize">
                        {appointment?.appointment_type?.replace(/_/g, ' ') || 'Medical Visit'}
                      </p>
                    </div>
                  </div>
                  
                  {appointment?.provider_name && (
                    <div className="flex items-center gap-3">
                      <User size={18} className="text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500">Provider</p>
                        <p className="font-medium text-slate-900">{appointment.provider_name}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right - Wait Time & Status */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 text-lg flex items-center gap-2">
                  <Clock size={20} className="text-teal-600" />
                  Wait Status
                </h3>
                
                <div className="bg-slate-50 rounded-xl p-6 text-center">
                  <p className="text-sm text-slate-500 mb-2">Time in Waiting Room</p>
                  <p className="text-4xl font-bold text-slate-900 font-mono">
                    {formatWaitTime(waitTime)}
                  </p>
                  
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="flex items-center justify-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${
                        waitingStatus === 'provider_ready' 
                          ? 'bg-green-500 animate-pulse' 
                          : 'bg-amber-500 animate-pulse'
                      }`} />
                      <span className="text-sm font-medium text-slate-700">
                        {waitingStatus === 'provider_ready' 
                          ? 'Provider Ready - Join Now!' 
                          : 'Waiting for Provider...'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Join Button */}
            <div className="text-center">
              <button
                onClick={joinCall}
                className={`px-8 py-4 rounded-xl text-lg font-semibold flex items-center justify-center gap-3 mx-auto transition-all ${
                  waitingStatus === 'provider_ready'
                    ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                    : 'bg-teal-600 hover:bg-teal-700 text-white'
                }`}
                data-testid="join-video-call-btn"
              >
                <VideoCamera size={24} weight="fill" />
                {waitingStatus === 'provider_ready' ? 'Join Video Call Now' : 'Enter Video Call'}
              </button>
              
              {waitingStatus !== 'provider_ready' && (
                <p className="mt-3 text-sm text-slate-500">
                  You can join now to test your camera and microphone
                </p>
              )}
            </div>

            {/* Help Section */}
            <div className="mt-8 pt-8 border-t border-slate-200">
              <h4 className="font-medium text-slate-900 mb-4">While You Wait:</h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <VideoCamera size={24} className="text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-700">Test your camera and microphone</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <ChatCircle size={24} className="text-purple-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-700">Review your symptoms and questions</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <Phone size={24} className="text-amber-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-700">Need help? Call (555) 123-4567</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
